import { normalizeHost } from './host'

export { normalizeHost }

export const ROOT_DOMAIN = 'evnttz.com.br'

/**
 * Subdomínios curtos de dev/produção cujo slug canônico é outro.
 * Ex.: `cindi.localhost:3120` → `gamificacao-congresso-cindi-2026`.
 */
export const DEV_SUBDOMAIN_ALIASES: Record<string, string> = {
  cindi: 'gamificacao-congresso-cindi-2026',
  cajazeiras: 'cajazeiras-expo-de-negocios-2026'
}

/** Slug canônico padrão quando `localhost:PORT` é acessado sem subdomínio em dev. */
export const DEFAULT_DEV_EVENT_SLUG = 'gamificacao-congresso-cindi-2026'

export function resolveDevEventSlug(input: string): string {
  return DEV_SUBDOMAIN_ALIASES[input] ?? input
}

/** Rótulo de subdomínio para montar `http://{label}.localhost:PORT` no `pnpm dev`. */
export function devHostLabelForSlug(slugOrAlias: string): string {
  const slug = resolveDevEventSlug(slugOrAlias)
  const alias = Object.entries(DEV_SUBDOMAIN_ALIASES).find(
    ([, mappedSlug]) => mappedSlug === slug
  )?.[0]

  return alias ?? slugOrAlias
}

function resolveSubdomainSlug(label: string): string {
  return DEV_SUBDOMAIN_ALIASES[label] ?? label
}

function isBareLocalhost(normalizedHost: string): boolean {
  return normalizedHost.split(':')[0] === 'localhost'
}

function getDevDefaultSlug(): string {
  const fromEnv =
    typeof import.meta !== 'undefined' && import.meta.env
      ? import.meta.env.VITE_DEV_EVENT_SLUG
      : undefined

  if (fromEnv) return resolveDevEventSlug(fromEnv)

  return DEFAULT_DEV_EVENT_SLUG
}

export const DOMAIN_MAP: Record<string, string> = {
  'gorn.evnttz.com.br': 'go-rn-2026',
  'gorn2026.evnttz.com.br': 'go-rn-2026',
  'cindi.evnttz.com.br': 'gamificacao-congresso-cindi-2026',
  'cajazeiras.evnttz.com.br': 'cajazeiras-expo-de-negocios-2026',
  'ctrle.evnttz.com.br': 'ctrl-e-2026',
  'gameesn.evnttz.com.br': 'esn-no-movimenta-sebrae-agosto',
  'energiasgorn.evnttz.com.br': 'polo-sebrae-de-energias-fatura-de-energia',
  'jornadaciee.evnttz.com.br': 'jornada-gamificada-ciee'
}

/**
 * Labels que existem sob o domínio raiz mas não são evento.
 *
 * `gamificacao` é o domínio guarda-chuva do próprio app: sem estar aqui, o
 * fallback de subdomínio devolvia o slug fantasma "gamificacao" e o app seguia
 * como se tivesse resolvido um evento que não existe.
 */
const RESERVED_SUBDOMAIN_LABELS = new Set([
  'www',
  '',
  'gamificacao',
  'app',
  'api',
  'admin'
])

function extractSubdomainSlug(normalizedHost: string): string | null {
  const hostname = normalizedHost.split(':')[0]

  if (!hostname || hostname === ROOT_DOMAIN || hostname === 'localhost') {
    return null
  }

  if (hostname.endsWith('.localhost')) {
    const label = hostname.slice(0, -'.localhost'.length)
    if (!label || RESERVED_SUBDOMAIN_LABELS.has(label)) return null
    return label
  }

  if (hostname.endsWith('.lvh.me')) {
    const label = hostname.slice(0, -'.lvh.me'.length)
    if (!label || RESERVED_SUBDOMAIN_LABELS.has(label)) return null
    return label
  }

  if (hostname.endsWith(`.${ROOT_DOMAIN}`)) {
    const prefix = hostname.slice(0, -`.${ROOT_DOMAIN}`.length)
    const label = prefix.split('.').filter(Boolean)[0]
    if (!label || RESERVED_SUBDOMAIN_LABELS.has(label)) return null
    return label
  }

  if (hostname.endsWith('.vercel.app')) {
    const prefix = hostname.slice(0, -'.vercel.app'.length)
    const label = prefix.split('.').filter(Boolean)[0]
    if (!label || RESERVED_SUBDOMAIN_LABELS.has(label)) return null
    return label
  }

  return null
}

export function resolveTenantSlug(host: string): string | null {
  const normalizedHost = normalizeHost(host)
  if (DOMAIN_MAP[normalizedHost]) return DOMAIN_MAP[normalizedHost]

  const subdomain = extractSubdomainSlug(normalizedHost)
  if (subdomain) return resolveSubdomainSlug(subdomain)

  if (isBareLocalhost(normalizedHost)) return getDevDefaultSlug()

  return null
}

/**
 * Reverse-lookup do `DOMAIN_MAP`: dado o slug do evento, qual host o serve.
 *
 * Usado pelo redirect dos convites legados, que chegam com o slug no path e sem
 * o host do evento. Como o middleware roda no edge sem acesso à api, o mapa é a
 * única fonte disponível.
 *
 * O mapa é N→1 e inclui hosts de desenvolvimento (`enej.lvh.me:3120`,
 * `localhost:3120` → `enej`), então só hosts sob o domínio raiz são candidatos —
 * caso contrário um convite de produção redirecionaria para `localhost`. Havendo
 * mais de um candidato (alias real, como `gorn` e `gorn2026`), vence o primeiro
 * declarado; qualquer um serve, e fixar a ordem torna o resultado determinístico.
 */
export function resolveHostForSlug(slug: string): string | null {
  if (!slug) return null

  const candidates = Object.entries(DOMAIN_MAP).filter(
    ([host, mappedSlug]) =>
      mappedSlug === slug && host.endsWith(`.${ROOT_DOMAIN}`)
  )

  return candidates.length ? candidates[0][0] : null
}
